import { qfRestGet } from './qfClient';

/**
 * Market-wide settle-over-settle OI changes for Market Overview.
 *
 * `GET /v1/steamroom/market-oi-changes` — Steamroom REST bearer, same token
 * as `/v1/contract-leaders`. Not `/v1/oi-changes` (Discord, service-token)
 * and not `/v1/market-oi-changes` (the path we asked for; QF namespaced it).
 *
 * ⚠️ `oi_change` / `prev_open_interest` are null when the prior session has
 * no row. Never render those as `+0`. A genuine unchanged position arrives
 * as `0` and is real data.
 */
export const MARKET_OI_CHANGES_PATH = '/v1/steamroom/market-oi-changes';

export interface OiChangeContract {
  symbol: string;
  option_type: 'Call' | 'Put';
  strike: number;
  expiry: string;
  open_interest: number;
  prev_open_interest: number | null;
  oi_change: number | null;
}

export interface OiChangesBoard {
  session_date: string;
  prev_session_date: string;
  contracts: OiChangeContract[];
}

/** ⚠️ The wire shape. `snake_case`. QF names the prior `prior_open_interest`. */
interface QfOiChange {
  symbol: string;
  option_type: 'Call' | 'Put';
  strike: number;
  expiry: string;
  open_interest: number;
  prior_open_interest: number | null;
  oi_change: number | null;
}

interface QfOiChangesResponse {
  session_date?: string;
  prev_session_date?: string;
  contracts?: QfOiChange[];
}

export type OiChangeFilter = 'all' | 'increases' | 'decreases';

/** Rows requested. The board shows 15 after filtering. Out of range is 422. */
export const OI_CHANGES_FETCH_LIMIT = 100;

function toContract(row: QfOiChange): OiChangeContract {
  return {
    symbol: row.symbol,
    option_type: row.option_type,
    strike: row.strike,
    expiry: row.expiry,
    open_interest: row.open_interest,
    prev_open_interest: row.prior_open_interest,
    oi_change: row.oi_change,
  };
}

export async function fetchOiChanges(
  limit: number = OI_CHANGES_FETCH_LIMIT
): Promise<OiChangesBoard> {
  const body = await qfRestGet<QfOiChangesResponse>(MARKET_OI_CHANGES_PATH, {
    limit: String(limit),
  });
  return {
    session_date: body?.session_date ?? '',
    prev_session_date: body?.prev_session_date ?? '',
    contracts: Array.isArray(body?.contracts) ? body.contracts.map(toContract) : [],
  };
}

/**
 * Board could not be dated. Distinct from a dated empty list (not settled yet).
 */
export function isOiChangesUnavailable(board: OiChangesBoard | null | undefined): boolean {
  if (!board) return false;
  return !board.session_date && board.contracts.length === 0;
}

/**
 * Filter then cap. Applied after the fetch so Increases/Decreases each get
 * their own top-N rather than a leftover of All's slice.
 */
export function rankOiChanges(
  contracts: OiChangeContract[],
  filter: OiChangeFilter,
  limit: number
): OiChangeContract[] {
  const kept = contracts.filter((row) => {
    if (row.oi_change == null) return false;
    if (filter === 'increases') return row.oi_change > 0;
    if (filter === 'decreases') return row.oi_change < 0;
    return true;
  });
  return [...kept]
    .sort((a, b) => Math.abs(b.oi_change ?? 0) - Math.abs(a.oi_change ?? 0))
    .slice(0, limit);
}
